// public/js/features/settings/settings-tracking.js
// "Tracking" settings tab — per-tenant behaviour for how movement is turned into
// trips. First setting: the trip-chaining stop threshold. Deliberately a broad
// tab so future tenant tracking settings (travel thresholds, geofence defaults,
// …) can live here too. Reached only by tenant administrators (manageTenants);
// each admin edits their own tenant(s), platform roles may edit any.
import { loadTenants, updateTenant } from '../../modules/tenants.js';
import { scopeTenantParam } from '../../modules/tenant-scope.js';
import { scopePromptHtml } from '../../ui/tenant-scope-widget.js';
import { escapeHtml } from '../../utils/escape.js';
import { helpIcon, initHelp } from '../../ui/help.js';

const DEFAULT_MIN = 240;

// In-app help for this tab — see docs/help-text.md.
const HELP = {
  split:
    '<b>Trip chaining — one journey or two?</b><br>' +
    'When a tracker stops moving, the <b>length</b> of that stop decides what happens next. A stop ' +
    '<b>shorter</b> than this many minutes counts as a pause: the trips before and after it merge into ' +
    '<b>one</b> journey. A <b>longer</b> stop ends the journey, and the next movement starts a <b>new</b> one.' +
    '<br><br>Leave empty for the platform default (' + DEFAULT_MIN + ' min = 4 h).' +
    '<br><br><b>Aviation:</b> A ferry flight with a 40-min fuel stop should read as <i>one</i> journey — keep ' +
    'the threshold above 40 min. An aircraft parked overnight and flown again next day is clearly <i>two</i>.',
};

export async function renderTrackingSettings(container) {
  container.innerHTML = `<div class="text-muted small p-2"><i class="fa-solid fa-spinner fa-spin me-1"></i> Loading…</div>`;
  let tenants = [];
  try { tenants = await loadTenants(); }
  catch { container.innerHTML = `<div class="alert alert-danger">Failed to load tenants.</div>`; return; }
  if (!tenants.length) { container.innerHTML = `<div class="alert alert-warning">No tenants available.</div>`; return; }

  // Global tenant scope (hard-coupled): the sidebar selector is the ONLY
  // tenant choice — under "All tenants" this page just prompts for a scope.
  const scopeTid = scopeTenantParam();
  const scopePinned = scopeTid && scopeTid !== 'unassigned' && tenants.some(t => t.tenantId === scopeTid);
  if (tenants.length > 1 && !scopePinned) {
    container.innerHTML = scopePromptHtml('tracking settings');
    return;
  }
  let selectedTenantId = scopePinned ? scopeTid : tenants[0].tenantId;

  const draw = () => {
    const tenant = tenants.find(t => t.tenantId === selectedTenantId) || tenants[0];
    const cur = tenant.tripStopSplitMin;
    container.innerHTML = `
      <div class="card shadow-sm" style="max-width:560px;">
        <div class="card-body">
          <h6 class="mb-1">Trip chaining</h6>
          <p class="text-muted small mb-3">
            Consecutive device trips separated by a stop <strong>shorter</strong> than this merge into ONE
            journey; a <strong>longer</strong> stop starts a new journey. Leave empty for the platform default
            (${DEFAULT_MIN} min). Example: <em>720</em> keeps an overnight delivery together as a single trip.
          </p>
          <label class="form-label small mb-1" for="trk-split">Split after a stop of (minutes)${helpIcon('split')}</label>
          <div class="input-group input-group-sm" style="max-width:280px;">
            <input id="trk-split" type="number" min="5" max="10080" class="form-control"
                   value="${cur ?? ''}" placeholder="${DEFAULT_MIN} (default)">
            <button id="trk-save" class="btn btn-primary">Save</button>
          </div>
          <div class="form-text">5–10080 minutes. Applies to trips derived from now on (and on the next rebuild).</div>
        </div>
      </div>`;

    initHelp(container, HELP);

    const sel = container.querySelector('#trk-tenant');
    if (sel) sel.addEventListener('change', () => { selectedTenantId = sel.value; draw(); });

    container.querySelector('#trk-save').addEventListener('click', async () => {
      const raw = container.querySelector('#trk-split').value.trim();
      const val = raw ? parseInt(raw, 10) : null;
      if (raw && (!Number.isFinite(val) || val < 5 || val > 10080)) {
        window.showToast('Enter 5–10080 minutes (or leave empty for the default)', 'warning');
        return;
      }
      const btn = container.querySelector('#trk-save');
      btn.disabled = true;
      try {
        await updateTenant(selectedTenantId, { tripStopSplitMin: val });
        const t = tenants.find(x => x.tenantId === selectedTenantId);
        if (t) t.tripStopSplitMin = val;   // keep the local copy in sync
        window.showToast('Trip-chaining threshold saved', 'success');
      } catch (e) {
        window.showToast('Save failed — please try again', 'danger');
      } finally {
        btn.disabled = false;
      }
    });
  };

  draw();
}
